import React, { useEffect, useState } from 'react'

// eslint-disable-next-line
import ReactDOM from 'react-dom'
import styled, { CSS, css, useTheme } from 'styled-components'
import ModalComponent from 'styled-react-modal'

import { APP_DEFAULT_STATE } from '@api/local'
import { Button, Heading, HeadingAlignment, IconEnum, Paragraph, Rule, Spacer, StaticImage } from '@atoms/index'
import { usePortal } from '@client/hooks/UsePortal'
import { ResponsivePXValue, StrongBoxShadow } from '@components/Theme'
import { useGetAppQuery } from '@hooks/api'
import { DeviceTypeEnum, ObjectFitEnum } from '@uctypes/api/globalTypes'

export type ModalVariant = 'text' | 'multimedia'

export enum ModalPositionEnum {
  TOP_LEFT = 'TOP_LEFT',
  TOP_RIGHT = 'TOP_RIGHT',
  BOTTOM_LEFT = 'BOTTOM_LEFT',
  BOTTOM_RIGHT = 'BOTTOM_RIGHT',
  CENTER_TOP = 'CENTER_TOP',
  CENTER_CENTER = 'CENTER_CENTER',
  CENTER_BOTTOM = 'CENTER_BOTTOM',
}

const TRANSITION_DURATION = 500

const StyledModal = ModalComponent.styled`
  display: flex;
  align-items: center;
  justify-content: center;
`

const TopLeft = css`
  position: fixed;
  top: 5vw;
  left: 10vw;
`

const TopRight = css`
  position: fixed;
  top: 5vw;
  right: 10vw;
`

const BottomLeft = css`
  position: fixed;
  bottom: 5vw;
  left: 10vw;
`

const BottomRight = css`
  position: fixed;
  bottom: 5vw;
  right: 10vw;
`

const CenterTop = css`
  position: fixed;
  top: 5vw;
`

const CenterCenter = css`
  position: fixed;
`

const CenterBottom = css`
  position: fixed;
  bottom: 5vw;
  bottom: max(5vw, calc(100vh - 100dvh + 16px));
`

const ModalContainer = styled.div < { $opacity: number, $isMultimediaVariant: boolean, $position?: ModalPositionEnum, $fullScreen?: boolean, $isNativeApplication?: boolean, $modalOverflow?: string, $maxWidth?: string, $popupDirection?: string }>`
  display: flex;
  flex-direction: column;
  z-index: 10;

  opacity: ${(props): number => props.$opacity};
  transform: ${(props) =>
    props.$popupDirection === 'horizontal'
      ? `translateX(${props.$opacity ? '0' : '100%'})`
      : `translateY(${props.$opacity ? '0' : '100%'})`};

  transition: all ${TRANSITION_DURATION}ms ease;

  ${(props): CSS => props.$fullScreen
    ? `
    align-items: stretch;
    justify-content: flex-start;
    width: 100vw;
    height: 100vh;
    height: 100dvh;
    position: fixed;
    top: 0;
    left: 0;
    overflow: hidden;
  `
    : `
    align-items: center;
    justify-content: center;
    max-height: 100vh;
    max-height: 100dvh;
    ${ResponsivePXValue('height', { mobile: '100vh' })}
    ${ResponsivePXValue('height', { mobile: '100dvh' })}
  `};

  ${(props): CSS => props.$fullScreen ? ResponsivePXValue('padding-top', { mobile: '0' }) : ResponsivePXValue('padding-top', { mobile: '16px' })};
  
  ${(props): CSS => props.$isMultimediaVariant ? ResponsivePXValue('max-width', { mobile: '100%' }) : undefined};
  ${(props): CSS => props.$isMultimediaVariant ? ResponsivePXValue('position', { mobile: 'absolute' }) : undefined};
  ${(props): CSS => props.$isMultimediaVariant ? ResponsivePXValue('top', { mobile: '0' }) : undefined};

  ${(props): CSS => {
    if (props?.$maxWidth) {
      return css`
        max-width: ${props.$maxWidth};
      `
    }
  }}
  ${StrongBoxShadow}
  ${(props): CSS => {
    if (props?.$position) {
      switch (props.$position) {
        case ModalPositionEnum.TOP_LEFT:
          return TopLeft
        case ModalPositionEnum.TOP_RIGHT:
          return TopRight
        case ModalPositionEnum.BOTTOM_LEFT:
          return BottomLeft
        case ModalPositionEnum.BOTTOM_RIGHT:
          return BottomRight
        case ModalPositionEnum.CENTER_TOP:
          return CenterTop
        case ModalPositionEnum.CENTER_CENTER:
          return CenterCenter
        case ModalPositionEnum.CENTER_BOTTOM:
          return CenterBottom
        default:
          return CenterCenter
      }
    }
  }}
`

const OuterContainer = styled.div < { $backdrop: boolean, $hasTitle: boolean, $resolutionAdapter: boolean, $isMultimediaVariant: boolean, $fullScreen: boolean, $isNativeApplication: boolean, $modalOverflow?: string, $minWidth?: string, $forceFullWidth?: boolean }>`
  position: relative;
  background-color: ${(props): string => props.theme.colors.whites.pureWhite};
  ${(props): CSS => props.$minWidth ? `min-width: ${props.$minWidth};` : (props.$isMultimediaVariant ? ResponsivePXValue('max-width', { desktop: 'min-content' }) : ResponsivePXValue('max-width', { desktop: '1152px' }))};
  ${(props): CSS => props.$backdrop && !props.$minWidth ? ResponsivePXValue('min-width', { mobile: '100vw', tablet: '100vw', desktop: '576px' }) : undefined};
  ${(props): CSS => props.$resolutionAdapter ? ResponsivePXValue('max-height', { desktop: 'CALC(90vh - 52px)' }) : ResponsivePXValue('max-height', { desktop: 'CALC(90vh - 72px' })};
  ${(props): CSS => props.$isNativeApplication ? 'max-height: inherit' : undefined};
  ${ResponsivePXValue('border-radius', { desktop: '8px' })};
  box-sizing: border-box;
  ${(props): CSS => props.$forceFullWidth
    ? `
    ${ResponsivePXValue('width', { mobile: '100vw', tablet: '100vw', desktop: '100vw' })}
    ${ResponsivePXValue('max-width', { mobile: '100vw', tablet: '100vw', desktop: '576px' })}
  `
    : ''};
  ${(props): CSS => props.$fullScreen
    ? 'flex: 1 1 auto; min-width: 0; min-height: 0; overflow: hidden; display: flex; flex-direction: column;'
    : 'max-height: 100vh; max-height: 100dvh; display: flex; flex-direction: column; min-height: 0; overflow: hidden;'};
`

const ContentContainer = styled.div<{ $contentOverflow: string, $backdrop: boolean, $hasTitle: boolean, $resolutionAdapter: boolean, $minWidth?: string, $forceFullWidth?: boolean, $fullScreen?: boolean }>`
  ${(props): CSS => props.$minWidth ? `min-width: ${props.$minWidth};` : (props.$backdrop ? ResponsivePXValue('min-width', { desktop: '576px' }) : undefined)};
  box-sizing: border-box;
  ${ResponsivePXValue('padding', { mobile: '16px 16px 24px 24px', tablet: '16px 16px 24px 20px', desktop: '16px 24px 24px 24px' })}
  ${ResponsivePXValue('margin-right', '4px')}
  ${(props): CSS => props.$fullScreen
    ? `
    flex: 1 1 auto;
    overflow-y: auto;
    min-width: 0;
    min-height: 0;
    width: 100%;
    padding-top: 0 !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
  `
    : 'max-height: 100vh; max-height: 100dvh; overflow-y: auto; min-height: 0;'}
  overflow-x: hidden;

  .title-rule {
     width: initial;
     ${ResponsivePXValue('margin-left', { mobile: '-16px -16px -16px -24px', tablet: '-16px -16px -16px -20px', desktop: '-24px' })}
     ${ResponsivePXValue('margin-right', { mobile: '-16px -16px -16px -24px', tablet: '-16px -16px -16px -20px', desktop: '-24px' })}
  }

  ::-webkit-scrollbar {
    ${ResponsivePXValue('width', { mobile: '5px', tablet: '5px', desktop: '5px' })}
    ${ResponsivePXValue('height', { mobile: '8px', tablet: '8px', desktop: '8px' })}
    background-color: ${(props): string => props.theme.colors.whites.pureWhite};
  }

  ::-webkit-scrollbar-thumb {
    background-color: ${(props): string => props.theme.colors.whites.silver};
  }
`

const ScrollContainer = styled.div<{ $fullScreen?: boolean, $isNativeApplication?: boolean }>`
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  ${(props) => props.$isNativeApplication ? ResponsivePXValue('padding-bottom', '70px') : ''}
`

const ImageContainer = styled.div`
  display: flex;
  width: 100%;
  min-height: 150px;

  picture {
    display: flex;
  }

  img {
    min-height: 150px;
  }
`
const HeaderContainer = styled.div<{ $headerContainerPadding?: string }>`
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  z-index: 3;
  background-color: ${(props): string => props.theme.colors.whites.pureWhite};
  ${(props) => props.$headerContainerPadding ? `padding: ${props.$headerContainerPadding};` : ''}

  &::before {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 0;
    right: 0;
    ${ResponsivePXValue('height', { mobile: '16px', tablet: '16px', desktop: '16px' })}
    background-color: ${(props): string => props.theme.colors.whites.pureWhite};
  }
`

const HeadingContainer = styled.div`
  display: flex;
  align-items: center;
  position: relative;

  .modal-title {
    flex: 1;
    text-align: center;
    font-weight: bold;
    overflow: hidden;
    text-overflow: ellipsis;
    ${ResponsivePXValue('padding', { mobile: '0 36px', tablet: '0 32px', desktop: '0 32px' })}  // space for close button
  }
  .close-button {
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    color: ${(props): string => props.theme.colors.whites.silver};
  }
`
export interface ModalProps {
  open: boolean
  title?: string
  subTitle?: string
  titleAlign?: HeadingAlignment
  staticId?: string
  variant?: ModalVariant
  position?: ModalPositionEnum
  objectFit?: ObjectFitEnum
  onClose?: (e?: React.MouseEvent<HTMLAnchorElement | HTMLDivElement> | Event) => void
  children: string | JSX.Element | JSX.Element[]
  allowClose?: boolean
  showCloseButton?: boolean
  backdrop?: boolean
  allowScroll?: boolean
  allowBackgroundClose?: boolean
  contentOverflow?: string
  modalOverflow?: string
  className?: string
  resolutionAdapter?: boolean
  lowerCaseTitle?: boolean
  fullscreen?: boolean
  maxWidth?: string
  minWidth?: string
  headerContainerPadding?: string
  popupDirection?: string
  forceFullWidth?: boolean
}

interface ModalState {
  isOpen: boolean
  opacity: number
  isClosing: boolean
  isOpening: boolean
  closeHovering: boolean
}

const DEFAULT_STATE: ModalState = {
  isOpen: false,
  opacity: 0,
  isClosing: false,
  isOpening: false,
  closeHovering: false,
}

export function Modal({
  open, title, subTitle, titleAlign = 'center', variant = 'text', position, staticId, objectFit, onClose,
  children, allowClose = true, showCloseButton = false, allowBackgroundClose = true, backdrop = true,
  allowScroll = false, contentOverflow, modalOverflow, resolutionAdapter = true, fullscreen = true,
  maxWidth, minWidth, headerContainerPadding, popupDirection = 'vertical', forceFullWidth = false,
}: ModalProps): JSX.Element {

  const [state, setState] = useState<ModalState>({ ...DEFAULT_STATE })
  const { data: appData = { app: { ...APP_DEFAULT_STATE } } } = useGetAppQuery()
  const theme = useTheme()
  const isNativeApplication = appData?.app?.isNativeApplication ?? false
  const isMultimediaVariant = variant === 'multimedia'

  const _handleClose = (): void => {
    if (open && onClose && allowClose) {
      onClose()
    }
  }

  const _handleBackgroundClose = (): void => {
    if (open && onClose && allowBackgroundClose) {
      onClose()
    }
  }

  const modalAfterOpen = (): void => {
    if (state.isOpening) {
      setTimeout(() => {
        setState((prevState) => ({ ...prevState, opacity: 1, isOpening: false }))
      })
    }
  }

  const modalBeforeClose = (): Promise<void> => {
    if (state.isClosing) {
      return new Promise<void>((resolve): void => {
        setState((prevState) => ({ ...prevState, opacity: 0, isClosing: false }))
        setTimeout(resolve, TRANSITION_DURATION)
      })
    } else {
      return new Promise<void>((resolve): void => {
        setTimeout(resolve, TRANSITION_DURATION)
      })
    }
  }

  useEffect(() => {
    if (open && !state.isOpen) {
      setState((prevState) => ({ ...prevState, isOpen: true, isOpening: true }))
    } else if (!open && state.isOpen) {
      setState((prevState) => ({ ...prevState, isOpen: false, isClosing: true }))
    }
  }, [open])

  const isDesktop = appData.app.deviceType === DeviceTypeEnum.DESKTOP || appData.app.deviceType === DeviceTypeEnum.ULTRA
  // Only use fullscreen on mobile devices
  const shouldFullscreen = fullscreen && !isDesktop

  const background = backdrop && isDesktop ? theme.colors.misc.modalBackground : theme.colors.misc.overlay

  const modalPosition = backdrop ? 'fixed' : 'initial'
  const width = backdrop ? '100vw' : 'initial'
  const height = backdrop ? '100vh' : 'initial'

  const style = isDesktop
    ? { position: modalPosition, width, height, background, opacity: state.opacity, transition: '0.5s all ease-in-out' }
    : { background }

  if (appData.app.deviceType === DeviceTypeEnum.MOBILE) {
    position = undefined
  }

  const modal = (
    <StyledModal
      isOpen={state.isOpen}
      backgroundProps={{ style }}
      // beforeOpen={modalBeforeOpen}
      afterOpen={modalAfterOpen}
      beforeClose={modalBeforeClose}
      // afterClose={modalAfterClose}
      onBackgroundClick={_handleBackgroundClose}
      allowScroll={allowScroll}
      onEscapeKeydown={_handleClose}>
      <ModalContainer
        $opacity={state.opacity}
        $position={position}
        $fullScreen={shouldFullscreen}
        $isMultimediaVariant={isMultimediaVariant}
        $isNativeApplication={isNativeApplication}
        $modalOverflow={modalOverflow}
        $maxWidth={maxWidth}
        $popupDirection={popupDirection}
      >
        <OuterContainer
          $modalOverflow={modalOverflow}
          $backdrop={backdrop}
          $hasTitle={!!title}
          $resolutionAdapter={resolutionAdapter}
          $isMultimediaVariant={isMultimediaVariant}
          $fullScreen={shouldFullscreen}
          $isNativeApplication={isNativeApplication}
          $minWidth={minWidth}
          $forceFullWidth={forceFullWidth}
        >
          <If condition={isMultimediaVariant}>
            <ImageContainer>
              <StaticImage
                staticId={staticId}
                objectFit={objectFit}
                mobileWidth={768}
                tabletWidth={768}
                desktopWidth={768} />
            </ImageContainer>
          </If>
          <ContentContainer
            $contentOverflow={contentOverflow}
            $backdrop={backdrop}
            $hasTitle={!!title}
            $resolutionAdapter={resolutionAdapter}
            $minWidth={minWidth}
            $forceFullWidth={forceFullWidth}
          >
            <If condition={!!title}>
              <HeaderContainer $headerContainerPadding={headerContainerPadding}>
                <HeadingContainer>
                  <Heading className='modal-title' variant='h2' align={titleAlign}>{title}</Heading>
                  {showCloseButton && (
                    <Button className="close-button" color="transparent" icon={IconEnum.CLOSE_OUTLINE} onClick={_handleClose} />
                  )}
                </HeadingContainer>
                <Spacer mobile='12px' desktop='16px' />
                <If condition={!!subTitle}>
                  <Paragraph variant='p1' align={titleAlign}>{subTitle}</Paragraph>
                  <Spacer mobile='4px' desktop='16px' />
                </If>
                <Rule className='title-rule' color={theme.colors.greys.platinum} />
                <Spacer mobile='4px' desktop='24px' />
              </HeaderContainer>
            </If>
            <ScrollContainer $fullScreen={shouldFullscreen} $isNativeApplication={isNativeApplication}>
              {children}
            </ScrollContainer>
          </ContentContainer>
        </OuterContainer>
      </ModalContainer>
    </StyledModal>
  )

  const target = usePortal('root-modal')
  if (target) {
    return ReactDOM.createPortal(modal, target)
  }

  return modal
}
